
import { useCallback, useMemo, useEffect, useContext } from 'react';
import _ from 'underscore';
import {
  useCurrencyCountriesMappings,
  useCountryCurrenciesMappings,
} from '@bingads-webui/overreact-cc';
import type {
  CountryCurrenciesMapping,
  CustomerVariables,
} from '@bingads-webui/overreact-cc';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import type { ServerContextLogActivity } from '@bingads-webui-campaign-react/server-context';
import { useCustomerLevelVariables, usePostponeReadVariables } from '@bingads-webui-campaign-react/basic-context-utils';

export type CurrencyCountryMappingInstrumentationActivity =
  Required<Pick<ServerContextLogActivity, 'trace'>>;

export interface UseCurrencyCountryMappingProps {
  customerLevelVariables?: CustomerVariables;
  instrumentationActivity?: CurrencyCountryMappingInstrumentationActivity;
  onFetchError?: (error: unknown) => void;
}

export interface CurrencyCountryMapping extends CountryCurrenciesMapping {
  defaultCurrency: string;
}

export interface UseCurrencyCountryMappingResult {
  getCountriesForCurrency(currency?: string | null): string[] | undefined;
  getCurrenciesForCountry(country?: string | null): string[] | undefined;
  getDefaultCurrencyForCountry(country?: string | null): string | undefined;
  isCurrencySupportedInCountry(country?: string | null, currency?: string | null): boolean | undefined;
  isUseCurrencyCountryMappingReady: boolean;
}

export type CurrencyCountryMappingPostponeReadConfig =
  NonNullable<Parameters<typeof useCurrencyCountriesMappings>[1]> &
  NonNullable<Parameters<typeof useCountryCurrenciesMappings>[1]>;

export const useCurrencyCountryMapping = (props?: UseCurrencyCountryMappingProps): UseCurrencyCountryMappingResult => {
  const {
    onFetchError = _.noop,
    instrumentationActivity,
  } = props || {};

  const serverContext = useContext(ServerContext);
  const { CurrentAccount } = serverContext;
  const customerLevelVariables = useCustomerLevelVariables();
  const postponeReadConfig =
    usePostponeReadVariables() as CurrencyCountryMappingPostponeReadConfig;

  const onErrorLog = useCallback((error: unknown) => {
    instrumentationActivity!.trace(`Error in useCurrencyCountryMapping: ${JSON.stringify(error)}`);
  }, [instrumentationActivity]);

  const onError = _.isFunction(onFetchError) ? onFetchError : onErrorLog;

  const [{ data: currencyCountriesMappings, error: currencyCountryMappingError }, { read: readCurrencyCountriesMappings }] =
    useCurrencyCountriesMappings(customerLevelVariables, postponeReadConfig);
  const [{ data: countryCurrenciesMappings, error: countryCurrencyMappingError }, { read: readCountryCurrenciesMappings }] =
    useCountryCurrenciesMappings(customerLevelVariables, postponeReadConfig);

  useEffect(() => {
    if (!currencyCountriesMappings && !currencyCountryMappingError && CurrentAccount) {
      readCurrencyCountriesMappings();
    }
  }, [CurrentAccount, currencyCountriesMappings, currencyCountryMappingError, readCurrencyCountriesMappings]);

  useEffect(() => {
    if (!countryCurrenciesMappings && !countryCurrencyMappingError && CurrentAccount) {
      readCountryCurrenciesMappings();
    }
  }, [CurrentAccount, countryCurrenciesMappings, countryCurrencyMappingError, readCountryCurrenciesMappings]);

  useEffect(() => {
    if (currencyCountryMappingError) {
      onError(currencyCountryMappingError);
    }
  }, [onError, currencyCountryMappingError]);

  useEffect(() => {
    if (countryCurrencyMappingError) {
      onError(countryCurrencyMappingError);
    }
  }, [onError, countryCurrencyMappingError]);

  const isUseCurrencyCountryMappingReady =
    useMemo(
      () => !_.isEmpty(currencyCountriesMappings) && !_.isEmpty(countryCurrenciesMappings),
      [countryCurrenciesMappings, currencyCountriesMappings]
    );

  const getCountriesForCurrency = useCallback((currency?: string | null) => {
    if (_.isEmpty(currency) || !_.isString(currency) || !isUseCurrencyCountryMappingReady) {
      return undefined;
    }

    const uppercaseCurrency = currency.toUpperCase();
    const currencyCountry = _.findWhere(currencyCountriesMappings, { currency: uppercaseCurrency });
    return currencyCountry && currencyCountry.countries;
  }, [currencyCountriesMappings, isUseCurrencyCountryMappingReady]);

  const findCurrenciesForCountry = useCallback((country?: string | null) => {
    if (_.isEmpty(country) || !_.isString(country) || !isUseCurrencyCountryMappingReady) {
      return undefined;
    }

    const c = country.toUpperCase();
    const countryCurrencyObject = _.findWhere(
      countryCurrenciesMappings as CurrencyCountryMapping[],
      { country: c }
    );
    return countryCurrencyObject;
  }, [countryCurrenciesMappings, isUseCurrencyCountryMappingReady]);

  const getCurrenciesForCountry = useCallback((country?: string | null) => {
    const countryCurrency = findCurrenciesForCountry(country);
    return countryCurrency && countryCurrency.currencies;
  }, [findCurrenciesForCountry]);

  const getDefaultCurrencyForCountry = useCallback((country?: string | null) => {
    const countryCurrency = findCurrenciesForCountry(country);

    const allowedCurrencies =
      _.get(countryCurrency, 'currencies') as CountryCurrenciesMapping['currencies'] | undefined;
    const defaultCurrency =
      _.get(countryCurrency, 'defaultCurrency') as string | undefined;

    return _.contains(allowedCurrencies, defaultCurrency) ? defaultCurrency : _.first(allowedCurrencies);
  }, [findCurrenciesForCountry]);

  const isCurrencySupportedInCountry = useCallback((country?: string | null, currency?: string | null) => {
    if (_.isEmpty(currency) || _.isEmpty(country) || !isUseCurrencyCountryMappingReady) { return undefined; }

    const validCountriesForCurrency = getCountriesForCurrency(currency);
    return _.contains(validCountriesForCurrency, country);
  }, [getCountriesForCurrency, isUseCurrencyCountryMappingReady]);

  return {
    getCountriesForCurrency, getCurrenciesForCountry, getDefaultCurrencyForCountry, isCurrencySupportedInCountry, isUseCurrencyCountryMappingReady,
  };
};
